import { BCMSRecord, BCMSStateManagerCRUD, useBCMSContext } from "@bcm-dev/bcms-vite";
import { PageContainer, PageContentView, WidgetContainer, WidgetView } from 'components/shared';
import Classes from "model";
import React from 'react';
import "./styles.css";
import "./mobile.css";
import TASPositivePayExceptionChecksDesktopView from "./components/TASPositivePayExceptionChecksDesktopView";
import TASPositivePayCheckExceptionsSearch from "./classes/TASPositivePayCheckExceptionsSearch";
import TASPositivePayCheckExceptionsSearchUI from "./classes/TASPositivePayCheckExceptionsSearchUI";
import TASPositivePayExceptionCheckForm from "./classes/TASPositivePayExceptionCheckForm";
import TASPositivePayExceptionCheckFormUI from "./classes/TASPositivePayExceptionCheckFormUI";
import TASPositivePayExceptionChecksMobileView from "./components/TASPositivePayExceptionChecksMobileView";

/**
 * @function TASPositivePayExceptionsPage
 * @return {React.Component}
 * @exported true
 * @description The "TAS Positive Pay Exceptions" page.
 */
export default function TASPositivePayExceptionsPage(props) {
  const context = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, context);
  const data = { ...getDefaultData(undefined, context), ...props.data ?? {} };
  const ui = { ...getDefaultUI(data.search), ...props.ui ?? {} };

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,

    onSaveClick: evt => {
      const search = state.data.search;
      const edited = search.userEditedCheckForms();
      const editedIDs = edited.reduce((acc, item) => {
        acc.push(item.id);
        return acc;
      }, []);

      let exceptionCheckForms = search.exceptionCheckForms.filter(item => {
        return !editedIDs.includes(item.id);
      });

      let editExceptionCheckForms = search.editExceptionCheckForms.filter(item => {
        return !editedIDs.includes(item.id);
      });

      
      const searchModified = TASPositivePayCheckExceptionsSearch.init({ ...search.record, exceptionCheckForms, editExceptionCheckForms });
      const filter = searchModified.filter();

      const uiFilter = filter.reduce((acc, item) => {
        acc.push(
          TASPositivePayExceptionCheckFormUI.init({ exceptionCheckForm: item })
        );
        return acc;
      }, []);

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "search" ], searchModified)
      .update([ "data", "exceptionCheckForms" ], filter)
      .update([ "ui", "exceptionCheckForms" ], uiFilter)
      .update([ "ui", "search", "didSaveChanges" ], { isActive: true, numberOfChangesSaved: editedIDs.length })
      .submit(setState);
    },

    onSearch: prop => val => {
      const search = state.data.search;
      // Don't perform operations if the account hasn't changed.
      if(val === search.account.id) { return; }

      const searchModified = TASPositivePayCheckExceptionsSearch.init({ ...search.record });
      searchModified[prop] = val;
      const filter = searchModified.filter();

      const uiFilter = filter.reduce((acc, item) => {
        acc.push(
          TASPositivePayExceptionCheckFormUI.init({ exceptionCheckForm: item })
        );
        return acc;
      }, []);

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "search" ], searchModified)
      .update([ "data", "exceptionCheckForms" ], filter)
      .update([ "ui", "exceptionCheckForms" ], uiFilter)
      .submit(setState);
    },

    onEdit: (id, prop) => val => {
      let editExceptionCheckForms = state.data.search.editExceptionCheckForms;
      let exceptionCheckFormsUI = state.ui.exceptionCheckForms;

      editExceptionCheckForms = editExceptionCheckForms.reduce((acc, item) => {
        if(item.id === id) {
          const currItem = TASPositivePayExceptionCheckForm.init(item.record);
          currItem[prop] = val;

          if(prop === "action" && val === "prompt") {
            currItem["returnReason"] = "";
            currItem["didClickReturnReason"] = undefined;
          }

          if(prop === "action" && val === "approve") {
            currItem["didClickReturnReason"] = undefined;
          }

          acc.push(currItem);
        } else {
          acc.push(item);
        }

        return acc;
      }, []);


      exceptionCheckFormsUI = exceptionCheckFormsUI.reduce((acc, item, index) => {
        if(item.exceptionCheckForm.id === id) {
          const currItem = TASPositivePayExceptionCheckForm.init({ ...item.exceptionCheckForm.record });
          currItem[prop] = val;

          if(prop === "action" && val === "prompt") {
            currItem["returnReason"] = "";
            currItem["didClickReturnReason"] = undefined;
          }

          if(prop === "action" && val === "approve") {
            currItem["didClickReturnReason"] = undefined;
          }

          const currItemUI = TASPositivePayExceptionCheckFormUI.init({ ...item.record, exceptionCheckForm: currItem });
          acc.push(currItemUI);
        } else {
          acc.push(item);
        }

        return acc;
      }, []);

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "search", "editExceptionCheckForms" ], editExceptionCheckForms)
      .update([ "ui", "exceptionCheckForms" ], exceptionCheckFormsUI)
      .submit(setState);
    },
 
    onMobileCloseButtonClick: id => evt => {
      let exceptionCheckForms = state.ui.exceptionCheckForms;
      exceptionCheckForms = exceptionCheckForms.reduce((acc, item) => {
        if(item.id === id) {
          acc.push(
            TASPositivePayExceptionCheckFormUI.init({ 
              ...item.record,
                mobile: { isActive: !item.mobile.isActive } 
            })
          );
        } else {
          acc.push(item)
        }
        return acc;
      }, []);

      BCMSStateManagerCRUD.init(state)
      .update([ "ui", "exceptionCheckForms" ], exceptionCheckForms)
      .submit(setState);
    },

    onReturnReasonClick: id => evt => {
      let editExceptionCheckForms = state.data.search.editExceptionCheckForms;
      let exceptionCheckFormsUI = state.ui.exceptionCheckForms;

      editExceptionCheckForms = editExceptionCheckForms.reduce((acc, item) => {
        if(item.id === id) {
          const currItem = TASPositivePayExceptionCheckForm.init(item.record);
          currItem["didClickReturnReason"] = true;
          acc.push(currItem);
        } else {
          acc.push(item);
        }

        return acc;
      }, []);

      exceptionCheckFormsUI = exceptionCheckFormsUI.reduce((acc, item, index) => {
        if(item.exceptionCheckForm.id === id) {
          const currItem = TASPositivePayExceptionCheckForm.init({ ...item.exceptionCheckForm.record });
          currItem["didClickReturnReason"] = true;

          const currItemUI = TASPositivePayExceptionCheckFormUI.init({ ...item.record, exceptionCheckForm: currItem });
          acc.push(currItemUI);
        } else {
          acc.push(item);
        }

        return acc;
      }, []);

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "search", "editExceptionCheckForms" ], editExceptionCheckForms)
      .update([ "ui", "exceptionCheckForms" ], exceptionCheckFormsUI)
      .submit(setState);
    }
  });

  return (
    <PageContainer className="tas-positive-pay-exceptions-page" state={ { data, ui } } controller={ controller }>
      <PageContent />
    </PageContainer>
  );
}

/**
 * @function getDefaultData
 * @return {OBJECT}
 * @exported true
 * @description Data object for the page.
 */
export const getDefaultData = (searchVals) => {
  let vals = {
      account: {id: "all", value: "all", name: "All" },
  };

  vals = { ...vals, ...searchVals ?? {} };
  const search = TASPositivePayCheckExceptionsSearch.init(vals);
  const filter = search.filter();

  return {
    search: search,
    exceptionCheckForms: filter
  };
};

/**
 * @function getDefaultUI
 * @return {OBJECT}
 * @exported true
 * @description UI object for the page.
 */
export const getDefaultUI = (search) => {
  const filter = search.filter();
  const exceptionCheckForms = filter.reduce((acc, item) => {
    acc.push(
      TASPositivePayExceptionCheckFormUI.init({ id: item.id, exceptionCheckForm: item, isEditing: true })
    );
    return acc;
  }, []);

  return {
    search: TASPositivePayCheckExceptionsSearchUI.init({ 
      search: search,
      didSearch:  { isActive: true },
      didSaveChanges: { isActive: false, numberOfChangesSaved: 0 },
      mobile: { isActive: true },
      account: { isActive: false },
      saveAndCancelButtons: { isActive: false }
    }),
    exceptionCheckForms: exceptionCheckForms
  };
};

/**
 * @function PageContent
 * @return {React.Component}
 * @exported true
 * @description Content for the page.
 */
function PageContent(props) {
  return (
    <PageContentView>
         <WidgetContainer className="tas-positive-pay-exceptions-widget">
            <WidgetView text="Exceptions">
                <TASPositivePayExceptionChecksDesktopView/>
                <TASPositivePayExceptionChecksMobileView/>
            </WidgetView>
        </WidgetContainer>
    </PageContentView>
  );
}
